// Use relative API calls since frontend and backend are on same domain
const API_BASE_URL = '/api';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Create headers with authentication
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Generic API request handler
const apiRequest = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers
      }
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        // Read response as text first, then try to parse as JSON
        const responseText = await response.text();
        console.error('Server response text:', responseText);
        
        if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
          errorMessage = 'Backend server is not responding. Please make sure the server is running on port 5000.';
        } else {
          try {
            const error = JSON.parse(responseText);
            errorMessage = error.message || errorMessage;
            console.error('Server returned error:', error);
          } catch (jsonParseError) {
            errorMessage = `Server error: ${response.status} ${response.statusText} - ${responseText}`;
          }
        }
      } catch (readError) {
        console.error('Error reading response:', readError);
        errorMessage = `Network error: ${response.status} ${response.statusText}`;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    // Network errors (server not reachable)
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Cannot connect to backend server. Please make sure the server is running on port 5000.');
    }
    
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// Authentication API
export const authAPI = {
  login: async (email, password) => {
    return apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  },

  signup: async (name, email, password) => {
    return apiRequest('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ name, email, password })
    });
  },

  // Get current user info with usage stats
  getMe: async () => {
    return apiRequest('/auth/me');
  }
};

// Notes API
export const notesAPI = {
  // Get all notes for the authenticated user
  getAll: async () => {
    return apiRequest('/notes');
  },

  // Create a new note
  create: async (noteData) => {
    return apiRequest('/notes', {
      method: 'POST',
      body: JSON.stringify(noteData)
    });
  },

  // Update an existing note
  update: async (noteId, noteData) => {
    return apiRequest(`/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(noteData)
    });
  },

  // Delete a note
  delete: async (noteId) => {
    return apiRequest(`/notes/${noteId}`, {
      method: 'DELETE'
    });
  }
};

// Folders API
export const foldersAPI = {
  // Get all folders for the authenticated user
  getAll: async () => {
    return apiRequest('/folders');
  },

  // Create a new folder
  create: async (folderData) => {
    return apiRequest('/folders', {
      method: 'POST',
      body: JSON.stringify(folderData)
    });
  },

  // Update an existing folder
  update: async (folderId, folderData) => {
    return apiRequest(`/folders/${folderId}`, {
      method: 'PUT',
      body: JSON.stringify(folderData)
    });
  },

  // Delete a folder
  delete: async (folderId) => {
    return apiRequest(`/folders/${folderId}`, {
      method: 'DELETE'
    });
  }
};

// Utility functions for offline support
export const offlineUtils = {
  // Check if user is online with actual connectivity test
  isOnline: async () => {
    if (!navigator.onLine) return false;
    
    try {
      // Test actual internet connectivity first (most reliable)
      const response = await fetch('https://www.google.com/favicon.ico', { 
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      return true; // If we reach here, we have internet connectivity
    } catch {
      try {
        // Fallback: try to reach our API
        const response = await fetch(`${API_BASE_URL}/health`, { 
          method: 'HEAD',
          timeout: 5000,
          cache: 'no-cache'
        });
        return response.ok;
      } catch {
        // Final fallback: if both fail, user might still be online but backend is down
        // Don't mark as offline unless we're sure there's no internet
        try {
          const response = await fetch('https://cloudflare.com/favicon.ico', { 
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-cache'
          });
          return true; // Internet works, backend might be temporarily down
        } catch {
          return false; // Truly offline
        }
      }
    }
  },

  // Simple synchronous check (faster but less reliable)
  isOnlineSync: () => navigator.onLine,

  // Save to local storage as backup
  saveToLocal: (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  },

  // Load from local storage
  loadFromLocal: (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
      return null;
    }
  },

  // Queue offline actions
  queueOfflineAction: (action) => {
    const queue = offlineUtils.loadFromLocal('offlineQueue') || [];
    queue.push({ ...action, timestamp: Date.now() });
    offlineUtils.saveToLocal('offlineQueue', queue);
  },

  // Process offline queue when back online
  processOfflineQueue: async () => {
    const queue = offlineUtils.loadFromLocal('offlineQueue') || [];
    if (queue.length === 0) return;

    console.log(`Processing ${queue.length} offline actions...`);
    
    for (const action of queue) {
      try {
        switch (action.type) {
          case 'CREATE_NOTE':
            await notesAPI.create(action.data);
            break;
          case 'UPDATE_NOTE':
            await notesAPI.update(action.id, action.data);
            break;
          case 'DELETE_NOTE':
            await notesAPI.delete(action.id);
            break;
          case 'CREATE_FOLDER':
            await foldersAPI.create(action.data);
            break;
          case 'UPDATE_FOLDER':
            await foldersAPI.update(action.id, action.data);
            break;
          case 'DELETE_FOLDER':
            await foldersAPI.delete(action.id);
            break;
        }
      } catch (error) {
        console.error('Failed to process offline action:', action, error);
      }
    }

    // Clear the queue after processing
    offlineUtils.saveToLocal('offlineQueue', []);
  }
}; 